import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { DAGRE_LAYOUT_PRESETS, DagreLayoutPreset } from './utils/workflowDagreLayout';

const PRESET_STORAGE_KEY = 'workflow_dagre_preset';

interface LayoutRefreshButtonProps {
  onRelayout: (preset: DagreLayoutPreset) => void;
  onUndoLayout?: () => void;
  canUndoLayout?: boolean;
}

export const LayoutRefreshButton: React.FC<LayoutRefreshButtonProps> = ({
  onRelayout,
  onUndoLayout,
  canUndoLayout = false,
}) => {
  const { darkMode } = useTheme();

  const [selectedPreset, setSelectedPreset] = useState<DagreLayoutPreset>(() => {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY) as DagreLayoutPreset | null;
    return stored && stored in DAGRE_LAYOUT_PRESETS ? stored : 'comfortable';
  });

  const [isAnimating, setIsAnimating] = useState(false);

  // Keep in sync if another part of the app changes the stored preset
  useEffect(() => {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY) as DagreLayoutPreset | null;
    if (stored && stored in DAGRE_LAYOUT_PRESETS && stored !== selectedPreset) {
      setSelectedPreset(stored);
    }
  }, []);

  const handleRelayout = () => {
    localStorage.setItem(PRESET_STORAGE_KEY, selectedPreset);
    setIsAnimating(true);
    onRelayout(selectedPreset);
    setTimeout(() => setIsAnimating(false), 900);
  };

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const preset = e.target.value as DagreLayoutPreset;
    setSelectedPreset(preset);
    localStorage.setItem(PRESET_STORAGE_KEY, preset);
  };

  return (
    <div className="flex items-center space-x-1">
      {/* Layout undo button */}
      <button
        onClick={onUndoLayout}
        disabled={!canUndoLayout}
        title="Undo last layout"
        className={`
          text-xs px-2 py-1 rounded border transition-all duration-200 font-medium
          ${canUndoLayout
            ? darkMode
              ? 'bg-gray-700 border-green-700 text-green-300 hover:bg-gray-600 hover:border-green-500'
              : 'bg-gray-100 border-green-300 text-green-800 hover:bg-gray-200 hover:border-green-500'
            : darkMode
              ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
              : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          }
        `}
      >
        ↶
      </button>

      {/* Preset dropdown */}
      <select
        value={selectedPreset}
        onChange={handlePresetChange}
        className={`
          text-xs px-2 py-1 rounded-l border-y border-l transition-colors
          ${darkMode
            ? 'bg-gray-800 border-green-700 text-green-300 hover:border-green-500 focus:border-green-500'
            : 'bg-white border-green-300 text-green-800 hover:border-green-500 focus:border-green-500'
          }
          focus:outline-none
        `}
        title="Layout preset"
      >
        {(Object.entries(DAGRE_LAYOUT_PRESETS) as [DagreLayoutPreset, typeof DAGRE_LAYOUT_PRESETS[DagreLayoutPreset]][]).map(
          ([key, val]) => (
            <option key={key} value={key}>
              {val.label}
            </option>
          )
        )}
      </select>

      {/* Re-layout button */}
      <button
        onClick={handleRelayout}
        title={`Re-layout graph (${DAGRE_LAYOUT_PRESETS[selectedPreset].label})`}
        className={`
          text-xs px-2 py-1 rounded-r border transition-all duration-200 font-medium
          ${darkMode
            ? 'bg-green-800/60 border-green-700 text-green-300 hover:bg-green-700 hover:shadow-green-500/30 hover:shadow-lg'
            : 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200 hover:shadow-green-300/40 hover:shadow-md'
          }
        `}
      >
        <span
          className={`inline-block ${isAnimating ? 'animate-spin' : ''}`}
          style={{ display: 'inline-block' }}
        >
          ⟳
        </span>
        {' '}Re-layout
      </button>
    </div>
  );
};
